<template>
  <div class="search">
    <div class="container">
      <div class="search__wrapper">
        <div class="search__block" v-if="products.length > 0">
          <h2 class="search__title">{{ t('search.products') }} {{ t('search.byRequest') }} "{{ searchQuery }}"</h2>
          <div class="search__grid">
            <product-card
                v-for="product in products"
                :key="product.node.uuid"
                :product="product.node"
            />
          </div>
        </div>
        <div class="search__block" v-if="categories.length > 0">
          <h2 class="search__title">{{ t('search.categories') }} {{ t('search.byRequest') }} "{{ searchQuery }}"</h2>
          <div class="search__grid">
            <router-link
                :to="translations.i18nRoute({ name: 'market', query: { categoriesSlugs: category.node.slug } })"
                class="category"
                v-for="category in categories"
                :key="category.node.uuid"
            >
              <div class="category__inner">
                <img :src="category.node.image" :alt="category.node.name">
              </div>
              <p>{{ category.node.name }}</p>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <transition name="opacity" mode="out-in">
      <product-view
          v-if="activeViewProduct"
          :product="activeViewProduct"
      />
    </transition>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {Products} from "@/core/api/products.js";
import {Categories} from "@/core/api/categories.js";
import {useI18n} from "vue-i18n";
import ProductCard from "@/components/cards/product-card.vue";
import ProductView from "@/components/cards/product-view.vue";
import {useAppStore} from "@/stores/app.js";
import translations from "@/core/helpers/translations.js";

const route = useRoute()
const {t} = useI18n()
const appStore = useAppStore()

const searchQuery = computed(() => {
  return route.params.searchQuery
})

const activeViewProduct = computed(() => {
  return appStore.activeViewProduct
})

const products = ref([])
const categories = ref([])

const getProductsByName = async () => {
  const res = await Products.getProducts(
      `first: 10` +
      `, name: "${searchQuery.value}"`
  )

  products.value = res.data?.products?.edges
}

const getCategoriesByName = async () => {
  const res = await Categories.getCategoryByName(searchQuery.value)

  categories.value = res.data.categories.edges
}

watch(
    () => route.params.searchQuery,
    async () => {
      products.value = [];
      categories.value = [];

      await getProductsByName()
      await getCategoriesByName()
    }
)

onMounted(async () => {
  await getProductsByName()
  await getCategoriesByName()
})
</script>

<style lang="scss" scoped>
.search {
  padding-top: 150px;

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 50px;
  }

  &__block {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  &__title {
    color: #222;
    font-size: 28px;
    font-weight: 700;
    line-height: 30px;
  }

  &__grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(225px, 225px));
    column-gap: 20px;
    row-gap: 30px;
    justify-content: space-between;
  }
}

.category {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  cursor: pointer;

  &__inner {
    background-color: #F5F5F5;
    border-radius: 50%;
    padding: 15px;
    width: 130px;
    height: 130px;
    display: grid;
    place-items: center;
    transition: 0.2s;

    & img {
      width: 100%;
    }
  }

  & p {
    color: #222;
    text-align: center;
    font-weight: 500;
    line-height: 20px;
  }

  @include hover {
    & div {
      filter: drop-shadow(0 0 0 rgba(0, 0, 0, 0.05)) drop-shadow(0 2px 5px rgba(0, 0, 0, 0.05)) drop-shadow(1px 10px 10px rgba(0, 0, 0, 0.04)) drop-shadow(2px 22px 13px rgba(0, 0, 0, 0.03)) drop-shadow(4px 40px 16px rgba(0, 0, 0, 0.01)) drop-shadow(6px 62px 17px rgba(0, 0, 0, 0.00));
    }
  }
}

@media (max-width: 600px) {
  .search {
    &__grid {
      justify-content: center;
    }
  }
}
</style>